/**
 * BookmarksBar - Horizontal bar of pinned saved views
 *
 * Displays below the main header, showing pinned saved views as clickable chips.
 * Clicking navigates to the view type page with filter_config applied as URL params.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Button, Space, Tooltip, message } from 'antd';
import {
  StarFilled,
  SettingOutlined,
  ProjectOutlined,
  FileTextOutlined,
  CalendarOutlined,
  InsertRowBelowOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { savedViewsApi, type SavedView } from '@api/saved-views';

const VIEW_TYPE_ICONS: Record<string, React.ReactNode> = {
  tasks: <ProjectOutlined />,
  templates: <FileTextOutlined />,
  calendar: <CalendarOutlined />,
  kanban: <InsertRowBelowOutlined />,
  search: <SearchOutlined />,
};

const VIEW_TYPE_PATHS: Record<string, string> = {
  tasks: '/tasks',
  templates: '/templates',
  calendar: '/calendar',
  kanban: '/kanban',
  search: '/search',
};

export default function BookmarksBar() {
  const navigate = useNavigate();
  const [pinnedViews, setPinnedViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPinnedViews();
  }, []);

  async function loadPinnedViews() {
    try {
      const views = await savedViewsApi.list(true);
      setPinnedViews(views);
    } catch {
      // Silently fail - bar just won't show
    } finally {
      setLoading(false);
    }
  }

  function handleViewClick(view: SavedView) {
    const basePath = VIEW_TYPE_PATHS[view.viewType] || '/tasks';
    const params = new URLSearchParams();
    params.set('saved_view', view.id);
    if (view.viewType === 'search' && view.filterConfig?.query) {
      params.set('q', view.filterConfig.query as string);
    } else if (view.filterConfig && Object.keys(view.filterConfig).length > 0) {
      params.set('filters', JSON.stringify(view.filterConfig));
    }
    if (view.sortConfig) {
      params.set('sort', JSON.stringify(view.sortConfig));
    }
    navigate(`${basePath}?${params.toString()}`);
  }

  async function handleUnpin(e: React.MouseEvent, viewId: string) {
    e.stopPropagation();
    try {
      await savedViewsApi.togglePin(viewId);
      setPinnedViews(prev => prev.filter(v => v.id !== viewId));
    } catch {
      message.error('Failed to unpin view');
    }
  }

  // Don't render bar if no pinned views and not loading
  if (!loading && pinnedViews.length === 0) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <div
      style={{
        padding: '4px 16px',
        background: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 36,
        overflow: 'hidden',
      }}
    >
      <StarFilled style={{ color: 'var(--status-warning)', fontSize: 14, flexShrink: 0 }} />
      <Space size={4} wrap={false} style={{ overflow: 'auto', flex: 1 }}>
        {pinnedViews.map(view => (
          <Tag
            key={view.id}
            icon={VIEW_TYPE_ICONS[view.viewType]}
            color="blue"
            style={{ cursor: 'pointer', margin: 0 }}
            onClick={() => handleViewClick(view)}
            closable
            onClose={(e) => handleUnpin(e as any, view.id)}
          >
            {view.name}
          </Tag>
        ))}
      </Space>
      <Tooltip title="Manage saved views">
        <Button
          type="text"
          size="small"
          icon={<SettingOutlined />}
          onClick={() => navigate('/settings/saved-views')}
          style={{ flexShrink: 0 }}
        />
      </Tooltip>
    </div>
  );
}
